<template>
  <v-dialog v-model="show" max-width="800px" persistent>
    <v-card>
      <v-card-title class="headline">
        Zarządzanie grupami komponentów
        <v-spacer></v-spacer>
        <v-btn outlined @click="showAddGroupModal = true" class="ml-4">
          <v-icon small left>mdi-plus</v-icon>
          Dodaj grupę
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-simple-table dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">Nazwa grupy</th>
                <th class="text-center">Wymagana liczba</th>
                <th class="text-center" style="width: 100px">Akcje</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="group in data.components" :key="group.id">
                <td>{{ group.name }}</td>
                <td class="text-center">{{ group.requiredCount }}</td>
                <td class="text-center">
                  <v-icon small @click="removeGroup(group)">
                    mdi-delete
                  </v-icon>
                </td>
              </tr>
              <tr v-if="!data.components || data.components.length === 0">
                <td colspan="3" class="text-center">Brak przypisanych grup komponentów</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>
      <v-card-actions class="pb-4 pr-4">
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" text @click="close">Zamknij</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Add Group Modal -->
    <v-dialog v-model="showAddGroupModal" max-width="600px" persistent>
      <v-card>
        <v-card-title class="headline">
          Wybierz grupę do dodania
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="searchText"
            dense
            append-icon="mdi-magnify"
            hide-details="auto"
            placeholder="Wyszukaj grupę..."
            class="mb-4"
          ></v-text-field>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Nazwa grupy</th>
                  <th class="text-center">Wymagana liczba</th>
                  <th class="text-center" style="width: 100px">Akcje</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="group in filteredAvailableGroups" :key="group.id">
                  <td>{{ group.name }}</td>
                  <td class="text-center">{{ group.requiredCount }}</td>
                  <td class="text-center">
                    <v-icon @click="addGroup(group)">
                      mdi-plus
                    </v-icon>
                  </td>
                </tr>
                <tr v-if="availableGroupsLoading">
                  <td colspan="3" class="text-center">
                    <v-progress-circular indeterminate size="24" class="mt-2"></v-progress-circular>
                  </td>
                </tr>
                <tr v-else-if="filteredAvailableGroups.length === 0">
                  <td colspan="3" class="text-center">Brak dostępnych grup do dodania</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
        <v-card-actions class="pb-4 pr-4">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="showAddGroupModal = false">Anuluj</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
export default {
  name: "OfferComponentGroupsModal",
  props: {
    value: Boolean,
    data: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showAddGroupModal: false,
      availableGroupsLoading: false,
      availableGroups: [],
      searchText: ''
    };
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    filteredAvailableGroups() {
      if (!this.searchText) return this.availableGroups;
      const searchLower = this.searchText.toLowerCase();
      return this.availableGroups.filter(group => 
        group.name.toLowerCase().includes(searchLower)
      );
    }
  },
  methods: {
    close() {
      this.show = false;
      this.showAddGroupModal = false;
      this.searchText = '';
    },
    async loadAvailableGroups() {
      this.availableGroupsLoading = true;
      try {
        const response = await this.$store.dispatch("getComponentGroups");
        // Filter out already assigned groups
        this.availableGroups = (response.data.content || []).filter(
          group => !this.data.components.some(assigned => assigned.id === group.id)
        );
      } catch (error) {
        console.error("Error loading available groups:", error);
        this.$store.dispatch("snackbar/setSnackbar", {
          color: "error",
          text: "Wystąpił błąd podczas wczytywania dostępnych grup"
        });
      } finally {
        this.availableGroupsLoading = false;
      }
    },
    async addGroup(group) {
      try {
        await this.$store.dispatch("addOfferComponentGroup", {
          offerId: this.data.id,
          componentGroupId: group.id
        });
        
        // Add the group to the local components array
        this.data.components.push(group);
        
        // Remove the group from available groups
        this.availableGroups = this.availableGroups.filter(g => g.id !== group.id);
        
        this.$store.dispatch("snackbar/setSnackbar", {
          color: "success",
          text: "Grupa została dodana"
        });
        this.showAddGroupModal = false;
        this.searchText = '';
        this.$emit('group-added');
      } catch (error) {
        console.error("Error adding group:", error);
        this.$store.dispatch("snackbar/setSnackbar", {
          color: "error",
          text: "Wystąpił błąd podczas dodawania grupy"
        });
      }
    },
    async removeGroup(group) {
      try {
        await this.$store.dispatch("removeOfferComponentGroup", {
          offerId: this.data.id,
          componentGroupId: group.id
        });
        
        // Remove the group from the local components array
        this.data.components = this.data.components.filter(g => g.id !== group.id);
        
        // Add the group back to available groups
        this.availableGroups.push(group);
        
        this.$store.dispatch("snackbar/setSnackbar", {
          color: "success",
          text: "Grupa została usunięta"
        });
        this.$emit('group-removed');
      } catch (error) {
        console.error("Error removing group:", error);
        this.$store.dispatch("snackbar/setSnackbar", {
          color: "error",
          text: "Wystąpił błąd podczas usuwania grupy"
        });
      }
    }
  },
  watch: {
    showAddGroupModal(val) {
      if (val) {
        this.loadAvailableGroups();
      } else {
        this.searchText = '';
      }
    }
  }
};
</script>

<style scoped>
.v-card__title {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
</style> 